import type {
  LisProposalChange,
  LisReviewConfig,
  LisReviewDetail,
  LisReviewRecipients,
  LisReviewSummary,
} from '@/api/lisReviewApi'

/**
 * Rules for the LIS result review UI, kept out of the components so they can
 * be unit tested (vitest runs in node, with no DOM).
 */

/** The one place "pending review" is orange. Badge, dot and button all use it. */
export const LIS_PENDING_CLASS = 'bg-orange-500 text-white'
export const LIS_PENDING_OUTLINE_CLASS = 'border-orange-400 text-orange-700 bg-orange-50'

/** URL param that opens the review dialog for one result. */
export const LIS_REVIEW_PARAM = 'lisReview'

export interface PendingScope {
  procedureId?: string | null
  submissionId?: string | null
  mrn?: string | null
}

/**
 * How many results are waiting in a scope. A narrower scope wins: asking about
 * a submission never reports the procedure's count. No scope means the tenant
 * total. Undefined summary (still loading) is zero, so nothing flashes orange.
 */
export function pendingCount(summary: LisReviewSummary | undefined, scope: PendingScope = {}): number {
  if (!summary) return 0
  if (scope.submissionId) return summary.bySubmission[scope.submissionId] ?? 0
  if (scope.procedureId) return summary.byProcedure[scope.procedureId] ?? 0
  if (scope.mrn) return summary.byMrn[scope.mrn] ?? 0
  return summary.pending
}

/**
 * Home tiles that carry the orange LIS queue count: Transplant, where results
 * are reviewed, and Integration, where they arrive (z8nrz7fcu4).
 */
export const LIS_QUEUE_TILE_ROUTES: readonly string[] = ['/procedures', '/integrations']

export function showsLisQueue(to: string): boolean {
  return LIS_QUEUE_TILE_ROUTES.includes(to)
}

/** "9+" past nine, so a long queue does not stretch the badge. */
export function formatPendingCount(count: number): string {
  return count > 9 ? '9+' : String(count)
}

export const CHANGE_LABEL: Record<LisProposalChange, string> = {
  new: 'New',
  changed: 'Changed',
  same: 'Already current',
  kept: 'Kept',
  blocked: 'Cannot be written',
}

/** Whether a proposal would write anything if approved. */
export function writesValue(change: LisProposalChange): boolean {
  return change === 'new' || change === 'changed'
}

/** URL param that opens a procedure page on the step holding one submission. */
export const LIS_SUBMISSION_PARAM = 'submission'

export interface LisFormLink {
  /** Opens the transplant on the form, with this review still open over it. */
  href: string
  label: string
  /** The form the link opens; null when only the transplant is known. */
  submissionId: string | null
}

/**
 * Where the review dialog's link goes: the transplant, opened on the form the
 * result writes to, with the dialog reopened there so it can be approved next
 * to the form. Prefers a form that would actually receive a value. Null when
 * the result is not bound to a patient's transplant.
 */
export function lisFormLink(detail: LisReviewDetail): LisFormLink | null {
  if (!detail.mrn || !detail.procedureId) return null
  const submissionId =
    detail.proposals.find((p) => p.submissionId && writesValue(p.change))?.submissionId ??
    detail.proposals.find((p) => p.submissionId)?.submissionId ??
    detail.submissionIds[0] ??
    null
  const qs = new URLSearchParams()
  if (submissionId) qs.set(LIS_SUBMISSION_PARAM, submissionId)
  qs.set(LIS_REVIEW_PARAM, detail.id)
  return {
    href: `/patients/${encodeURIComponent(detail.mrn)}/procedures/${encodeURIComponent(detail.procedureId)}?${qs}`,
    label: submissionId ? 'Go to form' : 'Go to transplant',
    submissionId,
  }
}

/** Why Approve is off, or null when it is available. Shown as the button title. */
export function approveBlockedReason(detail: LisReviewDetail, canEdit: boolean): string | null {
  if (!canEdit) return 'You need permission to edit submissions to approve a result.'
  if (detail.status !== 'PENDING') return 'This result has already been reviewed.'
  if (!detail.approvable) return 'Nothing in this result can be written to a form.'
  return null
}

export const RESULT_STATUS_LABEL: Record<string, string> = {
  F: 'Final',
  P: 'Preliminary',
  C: 'Corrected',
}

export function resultStatusLabel(code: string | null): string | null {
  if (!code) return null
  return RESULT_STATUS_LABEL[code.toUpperCase()] ?? code
}

/**
 * The failure sentences, each once, with how many values it applies to. The
 * server reports one per value, so seven values with no form read as the same
 * line seven times.
 */
export function groupFailures(failures: readonly { detail: string }[]): { detail: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const f of failures) counts.set(f.detail, (counts.get(f.detail) ?? 0) + 1)
  return [...counts].map(([detail, count]) => ({ detail, count }))
}

export function formatReviewValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'object' ? JSON.stringify(v) : String(v)
}

export const DEFAULT_LIS_REVIEW_CONFIG: LisReviewConfig = {
  channels: ['on_screen', 'email'],
  recipients: { mode: 'editors' },
  notifyActiveEditor: true,
}

/**
 * Switching mode starts the new selection empty: user ids and role ids are
 * different things, and carrying one across would half-map an audience.
 */
export function recipientsForMode(mode: LisReviewRecipients['mode']): LisReviewRecipients {
  if (mode === 'editors') return { mode: 'editors' }
  if (mode === 'roles') return { mode: 'roles', roleIds: [] }
  return { mode: 'members', userIds: [] }
}

/** A config that would notify nobody, with the sentence to tell the author. */
export function configProblem(config: LisReviewConfig): string | null {
  if (config.channels.length === 0) return 'Pick at least one channel.'
  if (config.recipients.mode === 'members' && config.recipients.userIds.length === 0) {
    return 'Pick at least one person.'
  }
  if (config.recipients.mode === 'roles' && config.recipients.roleIds.length === 0) {
    return 'Pick at least one role.'
  }
  return null
}
